// ✅ FILE: src/pages/what-we-do/focus-areas.js
import { Shield, Leaf, Sprout, BookOpen } from "lucide-react";

export const WHAT_WE_DO_BASE = "/what-we-do";

/**
 * ✅ IMPORTANT:
 * You had a "badge" style that was great for WHITE pages, but it becomes ugly on HERO images.
 * So we split badges into:
 *  - heroBadge: always glass + white text (works on dark hero images)
 *  - listBadge: subtle, readable on white sections/cards (WhatWeDo list + side nav)
 */
const HERO_BADGE = {
  wrap:
    "inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold tracking-wide text-white/95 backdrop-blur-sm shadow-[0_10px_30px_rgba(0,0,0,0.25)]",
  icon: "text-amber-300",
  dot: "h-2 w-2 rounded-full bg-amber-300",
};

const LIST_BADGE_BASE =
  "bg-stone-50 text-stone-700 border-stone-200 hover:bg-stone-100";

export const focusAreas = [
  // 1) Humanitarian & Child Protection
  {
    key: "humanitarian-child-protection",
    slug: "humanitarian-and-child-protection",
    shortLabel: "Humanitarian & Child Protection",
    icon: Shield,
    title: "Humanitarian and Child Protection",
    description:
      "We deliver rapid humanitarian assistance while strengthening child protection and safeguarding systems that uphold dignity, safety, and rights for vulnerable individuals and families.",
    image: "/assets/whatwedo-hero.png",
    heroOverlay: "from-slate-950/85 via-slate-900/65 to-black/60",

    // ✅ for WHITE pages
    listBadge: LIST_BADGE_BASE,

    // ✅ for HERO images (always readable)
    heroBadge: {
      ...HERO_BADGE,
      label: "Focus Area",
    },

    // ✅ design system you already use on cards/icons
    ring: "ring-slate-200/60",
    borderColor: "border-slate-200",
    color: "bg-slate-50 text-slate-800",
    iconRing: "ring-slate-100",

    details: {
      headline: "Rapid response and protection that preserves dignity",
      intro:
        "We respond quickly when people face crisis—while ensuring children and vulnerable persons are protected, supported, and connected to safe pathways. Our model combines immediate relief with safeguarding, referrals, and community-based prevention.",
      whyItMatters: [
        "Crisis increases risks of neglect, exploitation, trafficking, abuse, and school dropout—especially for children.",
        "Families under extreme hardship often face food insecurity, unstable shelter, and limited access to basic services.",
        "Protection requires both urgent support and long-term community systems that prevent harm.",
      ],
      whoWeServe: [
        "Orphans and Vulnerable Children (OVC) and at-risk children",
        "Low-income families facing shocks (illness, displacement, fire, flooding, loss of income)",
        "Widows, elderly persons, and persons living with disabilities in urgent need",
        "Crisis-affected households needing structured welfare support and referrals",
      ],
      keyActivities: [
        "Emergency food relief and essential welfare packages (food, hygiene items, basic supplies)",
        "Temporary support for urgent shelter needs (where feasible) and linkage to services",
        "Child safeguarding sessions, caregiver guidance, and child-friendly awareness activities",
        "Case identification, safe referrals, and follow-up support with relevant services (where available)",
        "Community sensitization on child protection, abuse prevention, and reporting pathways",
        "Seasonal/outreach relief interventions for high-need communities",
      ],
      ourApproach: [
        "Dignity-first response: support delivered respectfully, with privacy and fairness.",
        "Safeguarding by design: prevention, safe handling of cases, and child-centered participation.",
        "Community partnership: working with leaders, caregivers, and local structures to sustain protection.",
        "Referral-based support: linking families to available social, health, and protection services.",
      ],
      safeguardingCommitments: [
        "Zero tolerance for exploitation, abuse, or harm in any form.",
        "Child-safe procedures for engagement, consent, and documentation.",
        "Confidentiality and responsible referral practices.",
      ],
      outcomes: [
        "Reduced immediate vulnerability through timely relief support",
        "Improved safety awareness and child protection behaviors within communities",
        "Stronger referral pathways and community responsibility for safeguarding",
        "Stabilized households better positioned to recover from shocks",
      ],
      indicators: [
        "Households reached with emergency support and essential items",
        "Children supported through safeguarding sessions and protection awareness",
        "Referrals completed and follow-ups conducted (where available)",
        "Community sessions delivered on child protection and safe reporting pathways",
      ],
      howToSupport: [
        "Sponsor emergency response kits (food, hygiene materials, basic supplies)",
        "Support child protection education materials and safeguarding outreaches",
        "Partner for logistics, outreach coordination, or service referrals",
        "Volunteer for packaging, distribution support, and community sessions",
      ],
    },
    projectCategoryMatch: [
      "Humanitarian",
      "Humanitarian Relief",
      "Relief",
      "Child Protection",
      "Protection",
      "Safeguarding",
    ],
  },

  // 2) Climate Action / Community Resilience
  {
    key: "climate-resilience",
    slug: "climate-action-and-community-resilience",
    shortLabel: "Climate & Resilience",
    icon: Leaf,
    title: "Climate Action and Community Resilience",
    description:
      "We strengthen communities facing climate risks through awareness, cleaner-community programs, and resilience-building initiatives that reduce environmental and public health threats.",
    image: "/assets/whatwedo-climate.png",
    heroOverlay: "from-teal-950/85 via-teal-900/65 to-black/60",

    listBadge: "bg-teal-50 text-teal-800 border-teal-200 hover:bg-teal-100",

    heroBadge: {
      ...HERO_BADGE,
      label: "Focus Area",
    },

    ring: "ring-teal-200/60",
    borderColor: "border-teal-200",
    color: "bg-teal-50 text-teal-700",
    iconRing: "ring-teal-100",

    details: {
      headline: "Resilience where climate impacts hit hardest",
      intro:
        "Many communities face flooding, erosion, heat stress, pollution, and poor waste systems. We combine climate education with practical community action—helping households reduce risks, improve sanitation, and build resilience.",
      whyItMatters: [
        "Climate shocks worsen poverty, displacement, disease exposure, and school interruption.",
        "Unsafe environments increase preventable illnesses, especially for children and older adults.",
        "Resilience improves safety, livelihoods, and long-term community stability.",
      ],
      whoWeServe: [
        "Communities vulnerable to flooding, erosion, and environmental degradation",
        "Low-income households exposed to pollution and poor waste systems",
        "Youth groups and community volunteers leading local climate action",
      ],
      keyActivities: [
        "Climate awareness campaigns and environmental education sessions",
        "Cleaner-community action days (sanitation, waste sorting, clean-ups)",
        "Public hygiene promotion and safer household waste practices",
        "Community engagement with local leaders to sustain climate action",
        "Resilience guidance and early-risk awareness (where applicable)",
      ],
      ourApproach: [
        "Community-led solutions: we mobilize local volunteers and strengthen ownership.",
        "Practical action: awareness plus visible improvement through clean-up and sanitation drives.",
        "Partnership building: working with community leaders, youth groups, and institutions.",
      ],
      outcomes: [
        "Improved awareness and adoption of safer environmental practices",
        "Cleaner surroundings with reduced public health risks",
        "Stronger community-led structures to sustain climate action",
      ],
      indicators: [
        "Number of climate education sessions delivered",
        "Cleaner-community actions completed and households reached",
        "Volunteers mobilized and local partnerships activated",
        "Adoption of improved waste practices in target areas (where tracked)",
      ],
      howToSupport: [
        "Sponsor clean-up kits, PPE, sanitation tools, and waste materials",
        "Partner for community engagement and climate education initiatives",
        "Volunteer for outreach, awareness drives, and cleaner-community programs",
      ],
    },
    projectCategoryMatch: [
      "Climate Action",
      "Climate",
      "Community Resilience",
      "Environment",
      "WASH",
      "Sanitation",
    ],
  },

  // 3) Sustainable Agriculture
  {
    key: "sustainable-agriculture",
    slug: "sustainable-agriculture-and-food-sufficiency",
    shortLabel: "Sustainable Agriculture",
    icon: Sprout,
    title: "Sustainable Agriculture and Food Sufficiency",
    description:
      "We promote climate-smart, sustainable agriculture that strengthens rural livelihoods, improves food sufficiency, and reduces vulnerability through practical training and community-based support.",
    image: "/assets/whatwedo-agriculture.png",
    heroOverlay: "from-emerald-950/85 via-emerald-900/65 to-black/60",

    listBadge: "bg-emerald-50 text-emerald-900 border-emerald-200 hover:bg-emerald-100",

    heroBadge: {
      ...HERO_BADGE,
      label: "Focus Area",
    },

    ring: "ring-emerald-200/60",
    borderColor: "border-emerald-200",
    color: "bg-emerald-50 text-emerald-800",
    iconRing: "ring-emerald-100",

    details: {
      headline: "Food sufficiency, stronger incomes, resilient rural communities",
      intro:
        "Sustainable agriculture is essential for poverty reduction and community stability. We strengthen farming capacity through climate-smart practices that improve productivity, protect soil health, and enhance household food security.",
      whyItMatters: [
        "Food insecurity increases vulnerability, malnutrition, and poor learning outcomes for children.",
        "Climate pressures reduce yields and increase farming risks for smallholder households.",
        "Sustainable practices protect natural resources while improving long-term productivity.",
      ],
      whoWeServe: [
        "Rural smallholder farmers and farming households",
        "Women farmers and vulnerable adult caregivers sustaining households",
        "Youth interested in agriculture as a pathway to livelihoods",
      ],
      keyActivities: [
        "Training on climate-smart and sustainable farming techniques",
        "Soil health and water management practices for improved yields",
        "Basic post-harvest handling and loss reduction guidance",
        "Farmer groups support and peer learning sessions",
        "Food sufficiency and household nutrition awareness initiatives",
      ],
      ourApproach: [
        "Practical training: simple, scalable methods farmers can adopt immediately.",
        "Community engagement: strengthening farmer groups and local ownership.",
        "Resilience integration: linking agriculture with climate adaptation strategies.",
      ],
      outcomes: [
        "Improved household food security and reduced vulnerability",
        "Enhanced productivity through sustainable farming practices",
        "Strengthened rural livelihoods and income stability pathways",
      ],
      indicators: [
        "Farmers trained and supported in sustainable practices",
        "Community agriculture sessions delivered",
        "Households reached with food sufficiency and nutrition guidance",
        "Adoption of recommended practices (where tracked)",
      ],
      howToSupport: [
        "Sponsor training materials and farmer learning sessions",
        "Support basic tools, seed support, and demonstration activities (where applicable)",
        "Partner for agronomy expertise, mentorship, or field training support",
        "Volunteer as a facilitator, trainer, or rural outreach supporter",
      ],
    },
    projectCategoryMatch: [
      "Agriculture",
      "Sustainable Agriculture",
      "Food Security",
      "Food Sufficiency",
      "Livelihood",
      "Rural Development",
    ],
  },

  // 4) Education, Skills and Digital Inclusion
  {
    key: "education-skills-digital",
    slug: "education-skills-and-digital-inclusion",
    shortLabel: "Education & Digital Inclusion",
    icon: BookOpen,
    title: "Education, Skills and Digital Inclusion",
    description:
      "We expand access to education while building practical skills and digital literacy that prepare vulnerable children, youth, and adults for modern opportunity, employability, and self-sufficiency.",
    image: "/assets/whatwedo-education.png",
    heroOverlay: "from-blue-950/85 via-blue-900/65 to-black/60",

    listBadge: "bg-blue-50 text-blue-900 border-blue-200 hover:bg-blue-100",

    heroBadge: {
      ...HERO_BADGE,
      label: "Focus Area",
    },

    ring: "ring-blue-200/60",
    borderColor: "border-blue-200",
    color: "bg-blue-50 text-blue-700",
    iconRing: "ring-blue-100",

    details: {
      headline: "Access to learning and skills that unlock opportunity",
      intro:
        "We support vulnerable learners to stay in school and succeed—while also equipping youth and adults with practical skills and digital literacy needed for work, entrepreneurship, and modern life.",
      whyItMatters: [
        "Poverty and crisis often push children out of school and limit future opportunity.",
        "Skills and digital inclusion increase employability and strengthen household resilience.",
        "Education support becomes stronger when paired with life skills and technology readiness.",
      ],
      whoWeServe: [
        "Vulnerable children at risk of dropping out",
        "OVC learners needing school supplies and learning support",
        "Youth and young adults seeking skills, employability, and digital competence",
        "Vulnerable adults (including caregivers) who need practical skill pathways",
      ],
      keyActivities: [
        "Back-to-school support (uniforms, bags, books, learning kits)",
        "Learning materials distribution and educational support programs",
        "Mentorship, life-skills, and learning motivation initiatives",
        "Vocational and skills acquisition training (where applicable)",
        "Digital literacy and computer basics (productivity tools, online safety, employability)",
        "Career readiness sessions and structured opportunity guidance",
      ],
      ourApproach: [
        "Retention-focused support: helping learners stay in school and continue learning.",
        "Skills pathway design: practical skill sets aligned to real-world opportunity.",
        "Digital inclusion: closing gaps in access, confidence, and safe tech use.",
      ],
      outcomes: [
        "Improved enrollment, retention, and learning confidence",
        "Increased skills readiness for work and entrepreneurship pathways",
        "Improved digital competence and reduced digital exclusion",
      ],
      indicators: [
        "Children supported with school supplies and learning materials",
        "Mentorship and life-skills sessions delivered",
        "Participants trained in digital literacy and practical skills",
        "Improved school attendance/retention outcomes (where tracked)",
      ],
      howToSupport: [
        "Sponsor school supplies, scholarships, and learning materials",
        "Donate laptops, devices, or sponsor digital training sessions",
        "Partner for mentorship, internships, or training support",
        "Volunteer as a mentor, facilitator, or learning support coach",
      ],
    },
    projectCategoryMatch: [
      "Education",
      "Education Support",
      "Skills",
      "Digital Inclusion",
      "Technology Inclusion",
      "Tech",
      "ICT",
    ],
  },
];

export const focusAreaBySlug = (slug) => focusAreas.find((a) => a.slug === slug);